import {
    convertDecimalToPercentageForInputDisplay,
    convertPercentageForInputDisplayToDecimal,
    formatPercentageOnInputChange,
    formattedDollarsToCents,
    isEmpty,
    transformEnumToLabelValueOption,
} from '@fruitful/fruitils';
import {
    MoneyMapRuleContributionType,
    MoneyMapRuleGoalType,
} from '@fruitful/types';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { LabelRegular, PICKER_PLACEHOLDER } from 'smoothie';
import { z } from 'zod';

import {
    ControlledCurrencyRow,
    ControlledInputRow,
    ControlledPickerRow,
} from './ControlledFormRows';
import { FormModuleContainer } from './FormModuleContainer';

export const goalSettingsFormSchema = z.object({
    goalSettings: z
        .object({
            priority: z
                .string()
                .min(1)
                .refine(
                    (priority) => !isNaN(parseInt(priority)),
                    'Enter a valid number',
                ),
            description: z
                .string()
                .min(1, { message: 'Goal name is required' }),
            goalType: z
                .string()
                .min(1, { message: 'Goal type is required' })
                .regex(new RegExp(`^(?!${PICKER_PLACEHOLDER}$).+`), {
                    message: `"${PICKER_PLACEHOLDER}" is not a valid option`,
                }),
            contributionType: z
                .string()
                .min(1, { message: 'Contribution type is required' })
                .regex(new RegExp(`^(?!${PICKER_PLACEHOLDER}$).+`), {
                    message: `"${PICKER_PLACEHOLDER}" is not a valid option`,
                }),
            // Required for goal types "Build up target balance" and "Debt pay down"
            targetBalance: z.string().optional(),
            // Required when contribution type is PERCENTAGE_OF_REMAINING_FUNDS
            percentage: z.string().optional(),
            monthlyContributionMax: z.string().optional(),
            yearlyContributionMax: z.string().optional(),
        })
        .refine(
            ({ goalType, targetBalance, percentage }) => {
                const targetBalanceCents = !isEmpty(targetBalance)
                    ? formattedDollarsToCents(targetBalance as string)
                    : 0;
                const percentageDecimal = !isEmpty(percentage)
                    ? convertPercentageForInputDisplayToDecimal(
                          percentage as string,
                      )
                    : 0;

                const isBuildUpOrDebtPaydown =
                    goalType ===
                        (MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE as string) ||
                    goalType === (MoneyMapRuleGoalType.DEBT_PAY_DOWN as string);

                // Debt pay down / build target always need a positive balance so Income Split
                // can create target-based rules; percentage alone is not enough.
                if (isBuildUpOrDebtPaydown) {
                    return targetBalanceCents > 0;
                }

                return (
                    targetBalanceCents > 0 ||
                    (percentageDecimal > 0 && percentageDecimal <= 1)
                );
            },
            {
                message: 'Please enter a value greater than 0',
                path: ['goalSettings', 'targetBalance'],
            },
        )
        .refine(
            ({ contributionType, percentage }) => {
                // When contribution type is PERCENTAGE_OF_REMAINING_FUNDS, percentage is required
                if (
                    contributionType ===
                    MoneyMapRuleContributionType.PERCENTAGE_OF_REMAINING_FUNDS.toString()
                ) {
                    const percentageDecimal = !isEmpty(percentage)
                        ? convertPercentageForInputDisplayToDecimal(
                              percentage as string,
                          )
                        : 0;
                    return percentageDecimal > 0 && percentageDecimal <= 1;
                }
                return true;
            },
            {
                message: 'Percentage is required',
                path: ['percentage'],
            },
        ),
});

export type GoalSettingsFormSchema = z.infer<typeof goalSettingsFormSchema>;

interface GoalSettingsFormModuleProps<TContext> {
    formFunctions: UseFormReturn<GoalSettingsFormSchema, TContext>;
    showExternalAccountTargetBalanceWarning?: boolean;
    belongsToGroup?: boolean;
    /** True for any IM/Apex account (new or existing) — restricts goal type to "Contribute indefinitely". */
    isInvestmentManagementAccount?: boolean;
}

export const GoalSettingsFormModule = <TContext,>({
    formFunctions,
    showExternalAccountTargetBalanceWarning,
    belongsToGroup = false,
    isInvestmentManagementAccount = false,
}: GoalSettingsFormModuleProps<TContext>) => {
    const {
        control,
        register,
        watch,
        setValue,
        formState: { errors },
    } = formFunctions;

    const goalType = watch('goalSettings.goalType') as MoneyMapRuleGoalType;
    const contributionType = watch(
        'goalSettings.contributionType',
    ) as MoneyMapRuleContributionType;

    useEffect(() => {
        if (
            isInvestmentManagementAccount &&
            goalType !== MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY
        ) {
            setValue(
                'goalSettings.goalType',
                MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY,
            );
        }
    }, [isInvestmentManagementAccount, goalType, setValue]);

    useEffect(() => {
        if (
            contributionType ===
            MoneyMapRuleContributionType.ALL_REMAINING_FUNDS
        ) {
            setValue(
                'goalSettings.percentage',
                convertDecimalToPercentageForInputDisplay(1),
            );
        }
    }, [contributionType]);

    // Clear selections from hidden form fields when goal type is changed
    useEffect(() => {
        if (goalType === MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY) {
            setValue('goalSettings.targetBalance', ''); // Setting to undefined does not work (bug with react-hook-form)
        }
    }, [goalType]);

    const goalTypeOptions = isInvestmentManagementAccount
        ? transformEnumToLabelValueOption(MoneyMapRuleGoalType).filter(
              (option) =>
                  option.value ===
                  (MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY as string),
          )
        : transformEnumToLabelValueOption(MoneyMapRuleGoalType);

    return (
        <FormModuleContainer title="Contribution" collapsible={false}>
            <ControlledInputRow
                name="goalSettings.priority"
                label="Goal priority"
                control={control}
                register={register}
                errors={errors}
                disabled={belongsToGroup}
            />
            <ControlledPickerRow
                name="goalSettings.goalType"
                label="Goal type"
                placeholder={PICKER_PLACEHOLDER}
                control={control}
                register={register}
                errors={errors}
                options={goalTypeOptions}
                disabled={isInvestmentManagementAccount}
            />
            {(goalType === MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE ||
                goalType === MoneyMapRuleGoalType.DEBT_PAY_DOWN) && (
                <ControlledCurrencyRow
                    name="goalSettings.targetBalance"
                    placeholder="$"
                    control={control}
                    register={register}
                    errors={errors}
                    label={
                        goalType ===
                        MoneyMapRuleGoalType.BUILD_UP_TARGET_BALANCE
                            ? 'Target balance'
                            : 'Loan balance'
                    }
                />
            )}
            {contributionType ===
                MoneyMapRuleContributionType.PERCENTAGE_OF_REMAINING_FUNDS && (
                <ControlledInputRow
                    name="goalSettings.percentage"
                    placeholder="%"
                    control={control}
                    register={register}
                    errors={errors}
                    label="Percentage"
                    formatValueOnChange={formatPercentageOnInputChange}
                />
            )}
            <ControlledCurrencyRow
                name="goalSettings.monthlyContributionMax"
                placeholder="$"
                control={control}
                register={register}
                errors={errors}
                label="Monthly limit (optional)"
            />
            <ControlledCurrencyRow
                name="goalSettings.yearlyContributionMax"
                placeholder="$"
                control={control}
                register={register}
                errors={errors}
                label="Annual limit (optional)"
            />
            {showExternalAccountTargetBalanceWarning && (
                <LabelRegular color="$orange1000" padding={16}>
                    For target balance goals referencing external accounts, the
                    generated Income Split rule does not support a target
                    balance. The generated rule will be &quot;contribute
                    indefinitely&quot;.
                </LabelRegular>
            )}
        </FormModuleContainer>
    );
};

export default GoalSettingsFormModule;
